import React, { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge } from 'antd';
import { useSiteAdminAuth } from '@shared/providers/SiteAdminAuthContext';
import { useAdminTheme } from '@shared/providers/admin-theme/AdminThemeContext';
import { getSiteAdminTheme } from '@shared/providers/admin-theme/configs';
import { useNotificationUnreadCounts } from '@/hooks/admin';

/**
 * Site Admin Header Props
 */
interface SiteAdminHeaderProps {
  onMenuClick?: () => void;
}

/**
 * Site Admin Header Component
 *
 * Displayed site name (`siteName`) is taken directly from URL param `:site`.
 * site = 'super-admin' → "Super Admin 管理ポータル". When there is no URL site
 * (e.g. another route) fallback `themeConfig.siteName`.
 */
export const SiteAdminHeader: React.FC<SiteAdminHeaderProps> = ({ onMenuClick }) => {
  const { admin, logout } = useSiteAdminAuth();
  const { siteType, colors, themeConfig } = useAdminTheme();
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();
  /**
   * THE BELL COUNTS THE NEW 通知センター, not the legacy 同期通知 inboxes.
   *
   * One number over three tabs — マスタ同期 + レセプト同期 + 審査システムからの通知 — because that is what the
   * bell opens. A bell that counted the old five while pointing at the new screen would send the reader to a
   * list whose total never matches the badge that sent them there.
   */
  const { data: notificationUnread } = useNotificationUnreadCounts();
  const unreadCount =
    (notificationUnread?.master ?? 0) +
    (notificationUnread?.rezept ?? 0) +
    (notificationUnread?.inbound ?? 0);
  const isStaging = import.meta.env.VITE_APP_ENV === 'staging';

  const siteName = useMemo(() => {
    if (!site) return themeConfig.siteName;
    if (site === 'super-admin') return 'Super Admin 管理ポータル';
    return getSiteAdminTheme(site).siteName;
  }, [site, themeConfig.siteName]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header
      className="text-white shadow sticky top-0 z-[1100]"
      style={{ backgroundColor: isStaging ? '#000000' : colors.primary }}
    >
      <div className="px-4">
        <div className="relative flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            {/* Menu Toggle Button */}
            <button
              onClick={onMenuClick}
              className="p-2 rounded-md hover:bg-white/20 transition-colors"
              data-testid="site-admin-menu-toggle"
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <Link to={`/${siteType}/admin`} className="text-xl font-bold text-white">
              {siteName}
            </Link>
          </div>

          {isStaging && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded bg-white px-4 py-1 text-lg font-bold text-black shadow-sm">
              ステージング環境
            </div>
          )}

          {/* User Menu */}
          {admin && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-white">{admin.name}</span>
              {admin.is_full_admin && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">全権限</span>
              )}
              <button
                onClick={() => navigate(`/${site ?? siteType}/admin/notification_center`)}
                className="p-2 rounded-md hover:bg-white/20 transition-colors"
                aria-label="通知センター"
                data-testid="site-admin-notification-center-bell"
              >
                {/* Caps at 999, the same cap the tab badges use (AdminNotificationCenterTemplate::badgeText).
                    Two different caps on one screen would read as two different counts. */}
                <Badge count={unreadCount} overflowCount={999} size="small">
                  <i className="fa fa-bell text-lg text-white" aria-hidden="true" />
                </Badge>
              </button>
              <button
                onClick={handleLogout}
                style={{ backgroundColor: colors.secondary }}
                className="text-sm px-4 py-1.5 rounded text-white border border-white/30 transition-colors hover:opacity-90"
                data-testid="site-admin-logout-button"
              >
                ログアウト
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
